import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { updateSchema, UpdatePermissionValidationSchema } from "@/features/permissions/schemas/create.schema";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import useClientPermission from "../../hooks/useClientPermission";
import useUpdateClientPermission from "../../hooks/useUpdateClientPermission";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useQueryClient } from "@tanstack/react-query";
import TextArea from "@/common/components/TextArea";
import { ShowHeader } from "@/common/components/ShowHeader";

const ClientPermissionsUpdate = () => {
  const { id } = useParams();

  const goBack = useGoBack();
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const queryClient = useQueryClient();

  const {
    data: initialPermission,
    isLoading
  } = useClientPermission(id || '');

  const {
    mutate: updateClientPermission,
    isPending: isUpdateClientPermissionPending,
  } = useUpdateClientPermission({
    id: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientPermission', id] });
      goBack({ fallback: `/client-permissions/${id}`, searchLastRouteFromFallback: true, })
    },
    onError: (error) => {
      if (error?.response?.data?.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<UpdatePermissionValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      display_name: initialPermission?.display_name,
      description: initialPermission?.description,
    }
  });

  const onSubmit: SubmitHandler<UpdatePermissionValidationSchema> = (data) => updateClientPermission(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/client-permissions/${id}` }}
        titleClassName="font-normal text-lg text-white-400"
        subtitleClassName="font-normal text-lg"
        title='Permissões - Clientes'
        subtitle='Editar permissão'
        buttons={(
          <div className="flex gap-4">
            <Button
              variant='outline-error'
              onClick={() => goBack({ fallback: `/client-permissions/${id}`, searchLastRouteFromFallback: true })}
              disabled={isUpdateClientPermissionPending}
            >
              Cancelar
            </Button>

            <Button
              data-testid='update_permission'
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={isUpdateClientPermissionPending}
            >
              Confirmar
            </Button>
          </div>
        )}
      /> 

      {isLoading ? (
        <Box>Carregando...</Box>
      ) : (
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <p className="text-lg font-bold">
            Editar permissão
          </p>

          <div className="flex gap-2">
            <TextField
              label="Nome"
              markAsRequired
              data-testid="display_name_input"
              errorMessage={errors?.display_name?.message}
              defaultValue={initialPermission?.display_name}
              className="w-1/2"
              {...register('display_name')}
              disabled={isUpdateClientPermissionPending}
            />
            <TextField
              label="Classe de operação"
              data-testid="operation_klass_input"
              defaultValue={initialPermission?.operation_klass}
              className="w-1/2"
              disabled
            />
          </div>

          <TextField
            label="Descrição"
            markAsRequired
            data-testid="description_input"
            errorMessage={errors?.description?.message}
            defaultValue={initialPermission?.description}
            {...register('description')}
            disabled={isUpdateClientPermissionPending}
          />

          <TextArea
            label="Propiedades"
            placeholder="{}"
            defaultValue={JSON.stringify(initialPermission?.properties, null, 4)}
            rows={8}
            disabled
          />
        </Box>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default ClientPermissionsUpdate;
